import Vue from 'vue';
import Vuex from 'vuex';

import * as Firebase from 'firebase/app';
import axios from 'axios';
import firebaseApp from './firebaseApp';
import createEasyFirestore from 'vuex-easy-firestore';

Vue.use(Vuex);

const fireCandidates = {
  firestorePath: 'candidates',
  firestoreRefType: 'collection',
  moduleName: 'fireCandidates',
  statePropName: 'candidates',
  namespaced: true,
};

const fireNonCandidates = {
  firestorePath: 'noncandidates',
  firestoreRefType: 'collection',
  moduleName: 'fireNonCandidates',
  statePropName: 'candidates',
  namespaced: true,
};

firebaseApp
  .firestore()
  .enablePersistence()
  .then(() => {
    return true;
  })
  .catch((error) => {
    return error;
  });

const easyFirestore = createEasyFirestore([fireCandidates, fireNonCandidates], {
  logging: true,
  FirebaseDependency: Firebase,
});

interface ToothlyState {
  shoppingCart: {
    currentStep: number;
    email: string;
    firstName: string;
    lastName: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
    agree: boolean;
  };
  areYouCandidate: {
    reason: string;
    usedBraces: boolean;
    usesPermanentRetainer: boolean;
    interestOnFixing: string;
    splice: number;
    space: number;
    state: string;
    name: string;
    email: string;
    phone: string;
  };
  currentUser: {
    name: string;
    email: string;
    phone: string;
  };
}

const initialState: ToothlyState = {
  shoppingCart: {
    currentStep: 1,
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    agree: false,
  },
  areYouCandidate: {
    reason: '',
    usedBraces: false,
    usesPermanentRetainer: false,
    interestOnFixing: '',
    splice: 1,
    space: 1,
    state: '',
    name: '',
    email: '',
    phone: '',
  },
  currentUser: {
    name: '',
    email: '',
    phone: '',
  },
};

export default new Vuex.Store({
  state: initialState,
  mutations: {
    setAreYouCandidateAnswers(state: ToothlyState, answers) {
      state.areYouCandidate = answers;
    },
    setAreYouCandidateUsedBraces(state: ToothlyState, usedBraces) {
      state.areYouCandidate = {
        ...state.areYouCandidate,
        usedBraces,
      };
    },
    setAreYouCandidateUsesPermanentRetainer(
      state: ToothlyState,
      usesPermanentRetainer,
    ) {
      state.areYouCandidate = {
        ...state.areYouCandidate,
        usesPermanentRetainer,
      };
    },
    setAreYouCandidateSpace(state: ToothlyState, space: number) {
      state.areYouCandidate = {
        ...state.areYouCandidate,
        space,
      };
    },
    setAreYouCandidateSplice(state: ToothlyState, splice: number) {
      state.areYouCandidate = {
        ...state.areYouCandidate,
        splice,
      };
    },
    setCurrentUserEmail(state: ToothlyState, email: string) {
      state.currentUser = {
        ...state.currentUser,
        email,
      };
      state.areYouCandidate = {
        ...state.areYouCandidate,
        email,
      };
    },
    setCurrentUserName(state: ToothlyState, name: string) {
      state.currentUser = {
        ...state.currentUser,
        name,
      };
      state.areYouCandidate = {
        ...state.areYouCandidate,
        name,
      };
    },
    setCurrentTelephone(state: ToothlyState, phone: string) {
      state.currentUser = {
        ...state.currentUser,
        phone,
      };
      state.areYouCandidate = {
        ...state.areYouCandidate,
        phone,
      };
    },
    setShoppingCart(state: ToothlyState, cart) {
      state.shoppingCart = cart;
    },
    setShoppingCartStep(state: ToothlyState, step) {
      state.shoppingCart.currentStep = step;
    },
  },
  actions: {
    sendAnswersEmail(context) {
      const isCandidate = context.getters.getAreYouCandidate as boolean;
      const candidateInfo = context.getters.getAreYouCandidateAnswers;
      const data = {
        data: {
          candidate: isCandidate,
          name: candidateInfo.name,
          phone: candidateInfo.phone,
          email: candidateInfo.email,
          answers: {
            reason: candidateInfo.reason,
            usedBraces: candidateInfo.usedBraces,
            usesPermanentRetainer: candidateInfo.usesPermanentRetainer,
            interestOnFixing: candidateInfo.interestOnFixing,
            splice: candidateInfo.splice,
            space: candidateInfo.space,
            state: candidateInfo.state,
          },
        },
      };
      const answersEmailEndpoint =
        'https://us-central1-toothly-a3762.cloudfunctions.net/answersEmail';

      axios
        .post(answersEmailEndpoint, data)
        .then((response) => {
          console.info(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },
  },
  getters: {
    getAreYouCandidate: (state) => {
      return !(
        state.areYouCandidate.usesPermanentRetainer ||
        state.areYouCandidate.splice === 3
      );
    },
    getAreYouCandidateAnswers: (state) => {
      return state.areYouCandidate;
    },
    getCandidateIsValid: (state) => {
      const candidate = state.areYouCandidate;
      const user = state.currentUser;

      return (
        candidate.reason !== '' &&
        candidate.interestOnFixing !== '' &&
        user.email !== '' &&
        user.name !== ''
      );
    },
    getCurrentUser: (state) => {
      return state.currentUser;
    },
    getShoppingCart: (state) => {
      return state.shoppingCart;
    },
    getShoppingCartIsValid: (state) => {
      const cart = state.shoppingCart;
      return (
        cart.agree === true &&
        cart.email !== '' &&
        cart.firstName !== '' &&
        cart.address !== '' &&
        cart.city !== '' &&
        cart.state !== '' &&
        cart.zipCode !== '' &&
        cart.phone !== ''
      );
    },
    getShoppingCartStep: (state) => {
      return state.shoppingCart.currentStep;
    },
  },
  plugins: [easyFirestore],
});
